import React from 'react';
import { Modal, Button, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

const { Title } = Typography;

const HelpModal = ({ visible, onClose, auction }) => {
  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <QuestionCircleOutlined style={{ color: '#1890ff' }} />
          <span>How Our Auction Works</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Got it!
        </Button>
      ]}
      width={700}
      centered
    >
      <div style={{ padding: '8px 0' }}>
        <div style={{ marginBottom: '24px' }}>
          <Title level={4} style={{ color: '#1890ff', marginBottom: '16px' }}>
            🎯 Live Auction Instructions
          </Title>

          {/* Getting Started */}
          <div style={{ marginBottom: '20px' }}>
            <Title level={5} style={{ color: '#262626', marginBottom: '8px' }}>
              📋 Getting Started
            </Title>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
              <li>Select any lot from the grid to view details and start bidding</li>
              <li>Each lot has a countdown timer showing time remaining</li>
              <li>Place bids using the Bid x1, x2, or x5 buttons</li>
              <li>Use the heart icon to add lots to your favorites</li>
            </ul>
          </div>

          {/* Auction Types */}
          <div style={{ marginBottom: '20px' }}>
            <Title level={5} style={{ color: '#262626', marginBottom: '8px' }}>
              🔒 Auction Types
            </Title>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ background: '#f6ffed', padding: '12px', borderRadius: '6px', border: '1px solid #b7eb8f', flex: 1 }}>
                <div style={{ fontWeight: 'bold', color: '#389e0d', marginBottom: '4px' }}>🌐 Public Auction</div>
                <div style={{ fontSize: '13px', color: '#595959' }}>
                  Anyone can participate and bid on lots. No invitation required.
                </div>
              </div>
              <div style={{ background: '#fff2f0', padding: '12px', borderRadius: '6px', border: '1px solid #ffccc7', flex: 1 }}>
                <div style={{ fontWeight: 'bold', color: '#a8071a', marginBottom: '4px' }}>🔐 Private Auction</div>
                <div style={{ fontSize: '13px', color: '#595959' }}>
                  Only invited participants can bid. You must be invited and accept the invitation to participate.
                </div>
              </div>
            </div>
          </div>

          {/* Auction Timing */}
          <div style={{ marginBottom: '20px' }}>
            <Title level={5} style={{ color: '#262626', marginBottom: '8px' }}>⏱️ Auction Timing</Title>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ background: '#e6f7ff', padding: '12px', borderRadius: '6px', border: '1px solid #91d5ff', flex: 1 }}>
                <div style={{ fontWeight: 'bold', color: '#0958d9', marginBottom: '4px' }}>🔄 Simultaneous Auction</div>
                <div style={{ fontSize: '13px', color: '#595959' }}>
                  All lots are available for bidding at the same time. You can bid on multiple lots simultaneously.
                </div>
              </div>
              <div style={{ background: '#f0f9ff', padding: '12px', borderRadius: '6px', border: '1px solid #91d5ff', flex: 1 }}>
                <div style={{ fontWeight: 'bold', color: '#0958d9', marginBottom: '4px' }}>📋 Sequential Auction</div>
                <div style={{ fontSize: '13px', color: '#595959' }}>
                  Lots are sold one at a time in order. Only the current active lot can be bid on. Previous lots must be completed before the next one becomes available.
                </div>
              </div>
            </div>
          </div>

          {/* Bidding Rules */}
          <div style={{ marginBottom: '20px' }}>
            <Title level={5} style={{ color: '#262626', marginBottom: '8px' }}>⏰ Bidding Rules</Title>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
              <li><strong>Bid x1:</strong> Place a single increment bid</li>
              <li><strong>Bid x2:</strong> Place a bid that's 2 increments higher</li>
              <li><strong>Bid x5:</strong> Place a bid that's 5 increments higher</li>
              <li>Each bid extends the timer by {auction?.lot_close_time_seconds || 60} seconds</li>
              <li>You cannot bid on your own lots</li>
              <li><strong>Auction Owner:</strong> Can close lots with no bids using "Close Lot – No Bids" button</li>
              <li><strong>Close Lot Rules:</strong> Only available for active lots with no existing bids</li>
              <li><strong>Close Lot Process:</strong> Confirmation modal → Processing → Lot marked as "Timed Out"</li>
              <li><strong>Sequential Impact:</strong> Closing a lot automatically activates the next lot in sequence</li>
            </ul>
          </div>

          {/* Max-Bid Feature */}
          <div style={{ marginBottom: '20px' }}>
            <Title level={5} style={{ color: '#262626', marginBottom: '8px' }}>🤖 Max-Bid Feature</Title>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
              <li>Set an increment amount and maximum bid limit</li>
              <li>Max-bid will automatically outbid competitors</li>
              <li>Stops when you reach your maximum or win the lot</li>
              <li>You can stop max-bid at any time</li>
            </ul>
          </div>

          {/* Lot Status Colors */}
          <div style={{ marginBottom: '20px' }}>
            <Title level={5} style={{ color: '#262626', marginBottom: '8px' }}>🎨 Lot Status Colors</Title>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', background: '#1890ff', borderRadius: '2px' }}></div>
                <span style={{ fontSize: '12px' }}>Your winning bid</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', background: '#faad14', borderRadius: '2px' }}></div>
                <span style={{ fontSize: '12px' }}>Other bidders active</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', background: '#52c41a', borderRadius: '2px' }}></div>
                <span style={{ fontSize: '12px' }}>You won!</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', background: '#ff4d4f', borderRadius: '2px' }}></div>
                <span style={{ fontSize: '12px' }}>You lost</span>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div style={{ marginBottom: '20px' }}>
            <Title level={5} style={{ color: '#262626', marginBottom: '8px' }}>🔔 Notifications</Title>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
              <li><strong>Outbid:</strong> You'll see a red notification when someone outbids you</li>
              <li><strong>Success:</strong> Green notification when you successfully outbid someone</li>
              <li><strong>Competition:</strong> Blue notification when others are competing</li>
              <li>Sound notifications for subscribed lots</li>
            </ul>
          </div>

          {/* Pro Tips */}
          <div style={{ background: 'linear-gradient(135deg, #e6f7ff, #f0f9ff)', padding: '16px', borderRadius: '8px', border: '1px solid #91d5ff' }}>
            <Title level={5} style={{ color: '#0958d9', marginBottom: '8px' }}>💡 Pro Tips</Title>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.6', margin: 0 }}>
              <li>Subscribe to lots you're interested in for sound notifications</li>
              <li>Use the sidebar filters to organize lots by status</li>
              <li>Max-bid is great for competitive lots</li>
              <li>Watch the countdown timer - bids extend the time</li>
              <li>In sequential auctions, wait for your turn to bid</li>
              <li>Private auctions require invitation acceptance before bidding</li>
            </ul>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default HelpModal;
